import { useCanManageCompany } from "@/hooks/useCanManageCompany";
import { isInStock } from "@/lib/partStatus";
import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Plus, Save, Trash2, Warehouse, Printer, Grid3X3 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

import { useStorageLocations, useStorageBins, useUpdateBinDescription } from "@/hooks/useStorageBins";
import { countLayoutBins, formatBinAddress, generateLayoutBins, parseLevelCount, storageLayoutSummary, type StorageLayout } from "@/lib/binLevels";
import { useSpareParts } from "@/hooks/useSpareParts";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";

export default function StorageManagement() {
  const { t } = useTranslation("admin");
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { allowed: isAdmin, isLoading } = useCanManageCompany();
  const { data: locations = [] } = useStorageLocations();
  const { data: spareParts = [] } = useSpareParts();
  const [selectedLocId, setSelectedLocId] = useState<string | null>(null);
  const { data: bins = [] } = useStorageBins(selectedLocId || undefined);

  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({
    name: "", aisles: "1", bays_per_aisle: "1", levels_per_bay: "1", shelves_per_level: "1", bins_per_shelf: "1",
  });

  const [editingBinId, setEditingBinId] = useState<string | null>(null);
  const [draftDescription, setDraftDescription] = useState("");
  const updateBinDescription = useUpdateBinDescription();

  const saveDescription = async (binId: string) => {
    const current = bins.find((b) => b.id === binId)?.description || "";
    setEditingBinId(null);
    if (draftDescription.trim() === current.trim()) return;
    try {
      await updateBinDescription.mutateAsync({ id: binId, description: draftDescription });
    } catch (e: any) {
      toast({ title: t("toast.updateFailed"), description: e.message, variant: "destructive" });
    }
  };

  // Aisle, bay, level and shelf may be 0 ("not used", FIX-318); bins per shelf is at least 1.
  const formLayout: StorageLayout = {
    aisles: parseLevelCount(form.aisles, 0),
    bays_per_aisle: parseLevelCount(form.bays_per_aisle, 0),
    levels_per_bay: parseLevelCount(form.levels_per_bay, 0),
    shelves_per_level: parseLevelCount(form.shelves_per_level, 0),
    bins_per_shelf: parseLevelCount(form.bins_per_shelf, 1),
  };

  const occupancyMap = new Map<string, number>();
  spareParts.forEach((p) => {
    if (p.bin_id && isInStock(p.status)) occupancyMap.set(p.bin_id, (occupancyMap.get(p.bin_id) || 0) + 1);
  });


  const createLocation = useMutation({
    mutationFn: async () => {
      // Get user's company
      const { data: companyData, error: companyErr } = await supabase
        .rpc("get_user_company_id", { _user_id: user!.id });
      if (companyErr) throw companyErr;
      if (!companyData) throw new Error("No company assigned to user. Contact a superadmin.");

      const totalBins = countLayoutBins(formLayout);
      if (totalBins > 5000) throw new Error(`Layout would create ${totalBins} bins. Max 5000.`);

      const { data: loc, error: locErr } = await supabase
        .from("storage_locations")
        .insert({
          company_id: companyData,
          name: form.name.trim(),
          ...formLayout,
        })
        .select()
        .single();
      if (locErr) throw locErr;

      // Generate all bins; unused levels stay NULL and are left out of the bin code.
      const binsRows = generateLayoutBins(formLayout).map((b) => ({ location_id: loc.id, ...b }));
      // Batch insert in chunks of 1000
      for (let i = 0; i < binsRows.length; i += 1000) {
        const chunk = binsRows.slice(i, i + 1000);
        const { error } = await supabase.from("storage_bins").insert(chunk);
        if (error) throw error;
      }
      return loc;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["storage_locations"] });
      queryClient.invalidateQueries({ queryKey: ["storage_bins_all"] });
      setShowNew(false);
      setForm({ name: "", aisles: "1", bays_per_aisle: "1", levels_per_bay: "1", shelves_per_level: "1", bins_per_shelf: "1" });
      toast({ title: t("toast.locationCreated") });
    },
    onError: (e: any) => toast({ title: t("toast.createFailed"), description: e.message, variant: "destructive" }),
  });

  const deleteLocation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("storage_locations").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["storage_locations"] });
      setSelectedLocId(null);
      toast({ title: t("toast.locationDeleted") });
    },
    onError: (e: any) => toast({ title: t("toast.deleteFailed"), description: e.message, variant: "destructive" }),
  });

  if (isLoading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>;
  // Access enforced by the route guard in App.tsx.

  return (
    <div className="min-h-screen bg-background pb-24">
      <div className="px-6 pt-8 pb-6">
        <div className="flex items-center gap-2 mb-1">
          <Warehouse className="w-5 h-5 text-primary" />
          <h1 className="text-2xl font-bold text-foreground">{t("storage.title")}</h1>
        </div>
        <p className="text-muted-foreground">{t("storage.subtitle")}</p>
      </div>

      <div className="px-6 space-y-4">
        {!showNew ? (
          <Button variant="outline" className="gap-2 border-dashed w-full" onClick={() => setShowNew(true)}>
            <Plus className="w-4 h-4" /> {t("storage.addLocation")}
          </Button>
        ) : (
          <div className="bg-card rounded-xl border border-border p-5 space-y-3">
            <h3 className="font-semibold">{t("storage.newLocation")}</h3>
            <Input placeholder={t("storage.locationName")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <div><label className="text-xs text-muted-foreground">{t("storage.aisles")}</label><Input type="number" min="0" value={form.aisles} onChange={(e) => setForm({ ...form, aisles: e.target.value })} /></div>
              <div><label className="text-xs text-muted-foreground">{t("storage.baysPerAisle")}</label><Input type="number" min="0" value={form.bays_per_aisle} onChange={(e) => setForm({ ...form, bays_per_aisle: e.target.value })} /></div>
              <div><label className="text-xs text-muted-foreground">{t("storage.levelsPerBay")}</label><Input type="number" min="0" value={form.levels_per_bay} onChange={(e) => setForm({ ...form, levels_per_bay: e.target.value })} /></div>
              <div><label className="text-xs text-muted-foreground">{t("storage.shelvesPerLevel")}</label><Input type="number" min="0" value={form.shelves_per_level} onChange={(e) => setForm({ ...form, shelves_per_level: e.target.value })} /></div>
              <div><label className="text-xs text-muted-foreground">{t("storage.binsPerShelf")}</label><Input type="number" min="1" value={form.bins_per_shelf} onChange={(e) => setForm({ ...form, bins_per_shelf: e.target.value })} /></div>
            </div>
            <p className="text-xs text-muted-foreground">
              {t("storage.levelsUnusedHint", { defaultValue: "Set a level to 0 if this location doesn't use it. Bins need at least 1." })}
            </p>
            <p className="text-xs text-muted-foreground">
              {t("storage.willCreateBins", { count: countLayoutBins(formLayout) })}
              {" "}
              {t("storage.exampleCode", {
                code: formatBinAddress(
                  formLayout.aisles > 0 ? 1 : null,
                  formLayout.bays_per_aisle > 0 ? 1 : null,
                  formLayout.levels_per_bay > 0 ? 1 : null,
                  formLayout.shelves_per_level > 0 ? 1 : null,
                  1,
                ),
                defaultValue: "Example bin code: {{code}}",
              })}
            </p>
            <div className="flex gap-2">
              <Button onClick={() => createLocation.mutate()} disabled={!form.name.trim() || createLocation.isPending} className="gap-1">
                <Save className="w-4 h-4" /> {t("common.create")}
              </Button>
              <Button variant="outline" onClick={() => setShowNew(false)}>{t("common.cancel")}</Button>
            </div>
          </div>
        )}

        {locations.map((loc) => {
          const isOpen = selectedLocId === loc.id;
          return (
            <div key={loc.id} className="bg-card rounded-xl border border-border">
              <div className="p-5 flex items-center justify-between">
                <div className="flex-1">
                  <h3 className="font-semibold text-foreground">{loc.name}</h3>
                  <p className="text-sm text-muted-foreground">
                    {storageLayoutSummary(t, loc)}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => navigate(`/settings/storage/${loc.id}/labels`)} className="gap-1">
                    <Printer className="w-4 h-4" /> {t("storage.printLabels")}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setSelectedLocId(isOpen ? null : loc.id)} className="gap-1">
                    <Grid3X3 className="w-4 h-4" /> {isOpen ? t("storage.hideBins") : t("storage.viewBins")}
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => { if (confirm(t("storage.deleteConfirm", { name: loc.name }))) deleteLocation.mutate(loc.id); }} className="text-destructive">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              {isOpen && (
                <div className="border-t border-border p-5">
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                    {bins.map((b) => {
                      const occ = occupancyMap.get(b.id) || 0;
                      const editing = editingBinId === b.id;
                      return (
                        <div key={b.id} className={`p-2.5 rounded-lg border text-xs ${occ > 0 ? "bg-primary/5 border-primary/40" : "bg-muted/30 border-border"}`}>
                          <div className="flex items-center justify-between gap-2">
                            <span className={`font-mono font-medium ${occ > 0 ? "text-primary" : "text-foreground"}`}>{b.address}</span>
                            {occ > 0 && <span className="text-[10px] text-muted-foreground">{t("storage.partsCount", { count: occ })}</span>}
                          </div>
                          {editing ? (
                            <Input
                              autoFocus
                              className="mt-2 h-7 text-xs"
                              value={draftDescription}
                              placeholder={t("storage.addDescription")}
                              onChange={(e) => setDraftDescription(e.target.value)}
                              onBlur={() => saveDescription(b.id)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") { e.preventDefault(); (e.target as HTMLInputElement).blur(); }
                                if (e.key === "Escape") { setEditingBinId(null); }
                              }}
                            />
                          ) : (
                            <button
                              type="button"
                              onClick={() => { setEditingBinId(b.id); setDraftDescription(b.description || ""); }}
                              className={`mt-1.5 w-full text-left truncate hover:underline ${b.description ? "text-foreground" : "text-muted-foreground italic"}`}
                            >
                              {b.description || t("storage.addDescription")}
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

            </div>
          );
        })}

        {locations.length === 0 && !showNew && (
          <div className="text-center text-muted-foreground py-8">{t("storage.noLocations")}</div>
        )}
      </div>
    </div>
  );
}
